import { useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { NoBackend } from "@/components/common/NoBackend";
import { HorizontalCardCarousel } from "@/components/design/HorizontalCardCarousel";
import { MyListingApplicationsDocument, SortOrder } from "@/graphql/generated/operations";
import { formatDateTime } from "@/lib/format-date";
import { userErrorMessage } from "@/lib/user-error";
import { ListingReference } from "@/pages/share/listings/ListingReference";
import { LISTING_APPLICATION_STATE_LABELS } from "./listing-application-state";

// Die Zeile "Meine Bewerbungen" auf der SHARE-Seite: die neuesten eigenen Bewerbungen mit ihrem
// Zustand, im Aufbau der Ausschreibungskachel (Zustand als Eyebrow, Ausschreibung, Datum und
// Kennziffer). Jede Kachel führt zu "Meine Bewerbungen"; Zurückziehen und alles Weitere geschieht dort.
const INPUT = { order: { receivedOn: SortOrder.Desc }, first: 8 };

export function MyApplicationsShareRow() {
  const intl = useIntl();
  const { data, loading, error } = useQuery(MyListingApplicationsDocument, { variables: { input: INPUT } });
  const applications = data?.myListingApplications.nodes ?? [];

  if (loading && applications.length === 0) {
    return (
      <div className="box bg-surface p-5 smycles-field-meta text-ink-muted" aria-busy="true">
        <FormattedMessage id="common.loading" defaultMessage="Laden…" />
      </div>
    );
  }
  if (error && applications.length === 0) {
    return (
      <p role="alert" className="text-smycles-label text-ink-muted">
        <FormattedMessage
          id="share.landing.error"
          defaultMessage="Error: {message}"
          values={{ message: userErrorMessage(intl, error) }}
        />
      </p>
    );
  }
  if (applications.length === 0) {
    return (
      <NoBackend label={intl.formatMessage({ id: "share.applications.empty", defaultMessage: "Noch keine Bewerbung" })} />
    );
  }
  return (
    <HorizontalCardCarousel>
      {applications.map((application) => {
        const received = formatDateTime(application.receivedOn, intl.locale);
        return (
          <Link
            key={application.id}
            id={`share-application-${application.id}`}
            to="/share/applications"
            className="box block w-64 shrink-0 bg-surface p-4 transition hover:-translate-y-0.5"
          >
            <span className="block text-smycles-micro tracking-[0.2em] text-ink-muted">
              <FormattedMessage {...LISTING_APPLICATION_STATE_LABELS[application.state]} />
            </span>
            <span className="mt-0.5 block text-smycles-body font-medium">
              {application.listing.name || (
                <FormattedMessage id="share.listings.untitled" defaultMessage="Ohne Titel" />
              )}
            </span>
            <span className="mt-4 flex flex-wrap justify-between gap-x-3 gap-y-0.5 text-smycles-micro text-ink-muted">
              {received ? (
                <FormattedMessage
                  id="share.applications.receivedOn"
                  defaultMessage="Eingegangen am {date}"
                  values={{ date: received }}
                />
              ) : null}
              <ListingReference reference={application.listing.reference} />
            </span>
          </Link>
        );
      })}
    </HorizontalCardCarousel>
  );
}
